import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";

import type { MDXEditorMethods } from "@mdxeditor/editor";
import clsx from "clsx";

import { logger } from "~/lib/logger";

import { Button } from "./ui/button";

// AIDEV-NOTE: Lazy load MDXEditor to avoid ESM issues on server
// Track mounted state to prevent race conditions during dynamic imports
const mdxEditorImport = () => import("@mdxeditor/editor");

// AIDEV-NOTE: Type interface for lazy-loaded MDXEditor component props
interface LazyMDXEditorProps {
    editorRef: (ref: MDXEditorMethods | null) => void;
    markdown: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
    contentEditableClassName?: string;
    className?: string;
}

const ENCODING_DEBUG_PATTERNS = [
    "\u00e2\u20ac\u201d", // â€”
    "\u00e2\u20ac\u2122", // â€™
    "\u00e2\u20ac\u0153", // â€œ
    "\u00e2\u20ac", // â€
    "\u00c3", // Ã
];

type EncodingDebugWindow = Window & {
    __ifsEncodingDebugOnce?: Record<string, boolean>;
};

const inspectEncoding = (value: string | null | undefined) => {
    if (!value) {
        return {
            hasValue: false,
            length: 0,
            hasMojibake: false,
            firstMojibakeIndex: -1,
            firstEmDashIndex: -1,
            snippet: "",
        };
    }

    const firstMojibakeIndex = ENCODING_DEBUG_PATTERNS.reduce(
        (index, pattern) => {
            const patternIndex = value.indexOf(pattern);
            if (patternIndex === -1) return index;
            if (index === -1) return patternIndex;
            return Math.min(index, patternIndex);
        },
        -1,
    );
    const firstEmDashIndex = value.indexOf("\u2014");
    const anchor =
        firstMojibakeIndex >= 0
            ? firstMojibakeIndex
            : firstEmDashIndex >= 0
              ? firstEmDashIndex
              : 0;
    const snippetStart = Math.max(0, anchor - 40);
    const snippet = value.slice(snippetStart, snippetStart + 140);

    return {
        hasValue: true,
        length: value.length,
        hasMojibake: firstMojibakeIndex >= 0,
        firstMojibakeIndex,
        firstEmDashIndex,
        snippet,
    };
};

const logEncodingOnce = (key: string, payload: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const debugWindow = window as EncodingDebugWindow;
    debugWindow.__ifsEncodingDebugOnce ??= {};
    if (debugWindow.__ifsEncodingDebugOnce[key]) return;
    debugWindow.__ifsEncodingDebugOnce[key] = true;
    // AIDEV-NOTE: Temporary mojibake diagnostics. Remove after encoding investigation is complete.
    logger
        .withContext({ ...payload, encodingDebugKey: key })
        .info("ENCODING_DEBUG");
};

const LazyMDXEditor = lazy(async () => {
    const module = await mdxEditorImport();
    const {
        MDXEditor,
        headingsPlugin,
        listsPlugin,
        markdownShortcutPlugin,
        quotePlugin,
    } = module;

    // Return a component that uses the imported MDXEditor
    return {
        default: ({ editorRef, ...props }: LazyMDXEditorProps) => (
            <MDXEditor
                ref={editorRef}
                plugins={[
                    headingsPlugin(),
                    listsPlugin(),
                    quotePlugin(),
                    markdownShortcutPlugin(),
                ]}
                {...props}
            />
        ),
    };
});

// Preload function to trigger module loading
export const preloadMDXEditor = () => {
    // AIDEV-NOTE: Preloads MDXEditor module for faster initial render
    mdxEditorImport();
};

export const EditableContent = ({
    onSubmit,
    defaultValue,
    padding = 4,
    className,
    wrapperClassName,
    disabled = false,
    debugLabel,
}: {
    onSubmit: (value: string) => Promise<void>;
    defaultValue?: string;
    padding?: number;
    className: string;
    wrapperClassName?: string;
    disabled?: boolean;
    debugLabel?: string;
}) => {
    // Normalize undefined defaultValue to empty string so comparisons are stable
    const normalizedDefault = defaultValue ?? "";

    const [value, setValue] = useState(normalizedDefault);
    // Use union with null so React returns a mutable ref object
    const editorRef = useRef<MDXEditorMethods | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [editorMounted, setEditorMounted] = useState(false);
    // AIDEV-NOTE: Track if user has interacted with editor to avoid false "editing" state
    // MDXEditor fires onChange on mount with normalized markdown, which can differ from defaultValue
    const [userHasInteracted, setUserHasInteracted] = useState(false);
    // AIDEV-NOTE: Track mount time to ignore onChange calls during initialization period
    // MDXEditor may fire multiple onChange calls during setup; we ignore those in first 300ms
    const mountTimeRef = useRef<number>(0);
    const showEditingElements = showSaveSuccess || isEditing || isSubmitting;

    useEffect(() => {
        if (!debugLabel) return;
        logEncodingOnce(`${debugLabel}:editable-content-input`, {
            boundary: "EditableContent.defaultValue",
            debugLabel,
            defaultValue: inspectEncoding(normalizedDefault),
        });
    }, [debugLabel, normalizedDefault]);

    // Update value when defaultValue changes and we're not editing
    useEffect(() => {
        if (!showEditingElements) {
            setValue(defaultValue ?? "");
            setUserHasInteracted(false);
            mountTimeRef.current = Date.now();

            // Only update editor if it's mounted
            if (editorRef.current && editorMounted) {
                editorRef.current.setMarkdown(defaultValue ?? "");
            }
        }
    }, [
        defaultValue,
        isEditing,
        editorMounted,
        isSubmitting,
        showEditingElements,
    ]);

    const handleSave = () => {
        if (value !== normalizedDefault) {
            setIsSubmitting(true);
            onSubmit(value)
                .then(() => {
                    setIsSubmitting(false);
                    setIsEditing(false);

                    // Show success message
                    setShowSaveSuccess(true);

                    // Hide success message after 1 second
                    setTimeout(() => {
                        setShowSaveSuccess(false);
                    }, 1000);
                })
                .catch(() => {
                    setIsSubmitting(false);
                });
        }
    };

    // AIDEV-NOTE: Only set isEditing after user has interacted with the editor
    // This prevents false "editing" state from MDXEditor's initial onChange normalization
    useEffect(() => {
        if (!userHasInteracted) {
            return;
        }

        const trimByLineBreaks = (value: string) =>
            value
                .split("\n")
                .map((line) => line.trim())
                .join("\n");
        const normalizedValueTrimmedByLineBreaks = trimByLineBreaks(value);
        const normalizedDefaultTrimmedByLineBreaks =
            trimByLineBreaks(normalizedDefault);

        if (
            normalizedValueTrimmedByLineBreaks !==
            normalizedDefaultTrimmedByLineBreaks
        ) {
            setIsEditing(true);
        } else {
            setIsEditing(false);
        }
    }, [value, normalizedDefault, userHasInteracted]);

    const contentEditableClassName = useMemo(
        () =>
            clsx(
                !className?.includes("font-display") && "font-body",
                className,
                `-ml-${padding} -mr-${padding} pl-${padding} pr-${padding}`,
                "rounded-md flex flex-col gap-4",
                disabled
                    ? "cursor-default pointer-events-none"
                    : `focus:outline-none focus:border-2 focus:border-brand-terra-100 focus:bg-[#fffbfb] focus:z-1 focus:ring-0 focus:ring-offset-0`,
                showEditingElements && !disabled
                    ? "border-2 border-brand-terra-100 bg-[#fffbfb] rounded-br-none"
                    : "border-2 border-transparent",
            ),
        [className, disabled, padding, showEditingElements],
    );

    // // Only render on client side
    // if (typeof window === "undefined") {
    //     return (
    //     );
    // }
    const fallback = (
        <div className={clsx("relative", wrapperClassName)}>
            <div className={contentEditableClassName}>
                {defaultValue
                    ?.split("\n\n")
                    .map((line, index) => <div key={index}>{line}</div>)}
            </div>
        </div>
    );

    return (
        <div className={clsx("relative", wrapperClassName)}>
            <Suspense fallback={fallback}>
                {!editorMounted && fallback}
                <LazyMDXEditor
                    editorRef={(editor) => {
                        const wasAlreadyMounted = editorRef.current !== null;
                        editorRef.current = editor;
                        // AIDEV-NOTE: Only reset mount time on initial mount, not on re-renders
                        // React calls callback refs on every render if the function identity changes,
                        // so we check if the editor was already mounted to avoid resetting state mid-save
                        if (editor && !wasAlreadyMounted) {
                            mountTimeRef.current = Date.now();
                            setUserHasInteracted(false);
                        }
                        setEditorMounted(!!editor);
                    }}
                    markdown={value || ""}
                    onChange={(newValue: string) => {
                        if (debugLabel) {
                            logEncodingOnce(
                                `${debugLabel}:editable-content-onchange`,
                                {
                                    boundary: "EditableContent.onChange",
                                    debugLabel,
                                    value: inspectEncoding(newValue),
                                    changedFromDefault:
                                        newValue !== normalizedDefault,
                                },
                            );
                        }
                        if (!disabled) {
                            setValue(newValue);
                            // AIDEV-NOTE: Ignore onChange calls within 300ms of mount
                            // MDXEditor fires multiple onChange during initialization
                            const timeSinceMount =
                                Date.now() - mountTimeRef.current;
                            if (timeSinceMount > 300) {
                                setUserHasInteracted(true);
                            }
                        }
                    }}
                    readOnly={disabled}
                    contentEditableClassName={contentEditableClassName}
                    className={clsx(
                        "focus:border border-brand-forest-800 ",
                        className,
                        disabled && "pointer-events-none",
                        !editorMounted && "hidden",
                    )}
                />
            </Suspense>
            {/* AIDEV-NOTE: z-50 ensures toolbar appears above sticky header (z-30) and other elements */}
            <div
                className={clsx(
                    "absolute right-0 top-[100%] flex gap-2 z-50",
                    "bg-brand-terra-50 border-2 border-brand-terra-100 p-2 rounded-b-md p-t-0",
                    (!showEditingElements || disabled) && "hidden",
                )}
                style={{ borderTop: "1px solid #FDF4F1" }}
            >
                {!showSaveSuccess && (
                    <Button
                        size="sm"
                        variant="ghost"
                        className="font-body"
                        onClick={() => {
                            setValue(defaultValue ?? "");
                            setIsEditing(false);
                            setUserHasInteracted(false);
                            mountTimeRef.current = Date.now();
                            if (editorRef.current) {
                                editorRef.current.setMarkdown(
                                    defaultValue ?? "",
                                );
                            }
                        }}
                    >
                        Cancel
                    </Button>
                )}
                {!showSaveSuccess && (
                    <Button
                        size="sm"
                        className="font-body bg-brand-terra-500 text-brand-terra-50 hover:bg-brand-terra-600"
                        onClick={handleSave}
                        disabled={isSubmitting}
                        isLoading={isSubmitting}
                    >
                        {isSubmitting ? "Saving..." : "Save changes"}
                    </Button>
                )}
                {showSaveSuccess && (
                    <Button size="sm" className="font-body gap-4">
                        <span className="text-sm font-medium">
                            Changes saved!
                        </span>
                    </Button>
                )}
            </div>

            {/* Success message notification */}
        </div>
    );
};
